import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { generateTeamCode } from '../../utils/auth';
import apiService from '../../services/api';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPlus, FiUsers, FiKey } = FiIcons;

const TeamSetup = () => {
  const [mode, setMode] = useState(''); // 'create' or 'join'
  const [formData, setFormData] = useState({
    teamName: '',
    teamCode: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();

  // Redirect if user already has a team
  useEffect(() => {
    if (user?.hasTeam) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleCreateTeam = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const teamCode = generateTeamCode();
      
      // 1. Create Team
      const teamResponse = await apiService.createTeam({
        name: formData.teamName,
        code: teamCode
      });

      if (teamResponse.status === 'success') {
        // 2. Link User to Team
        await apiService.createUserTeamRelation({
          user_id: user.id,
          team_id: teamResponse.id
        });

        // 3. Update User to be Team Owner
        const userUpdateData = {
          mail: user.mail,
          firstname: user.firstname,
          lastname: user.lastname,
          password: user.password,
          is_admin: user.is_admin || 0,
          is_team_owner: 1
        };

        await apiService.updateUser(user.id, userUpdateData);

        // 4. Update Local Context
        updateUser({
          ...user,
          hasTeam: true,
          teamId: teamResponse.id,
          is_team_owner: 1
        });

        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Create team error:', error);
      const errorMessage = error.message || 'Fehler beim Erstellen des Teams';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleJoinTeam = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const teamResponse = await apiService.searchTeams({
        code: formData.teamCode
      });

      if (teamResponse.data && teamResponse.data.length > 0) {
        const team = teamResponse.data[0];
        
        // Add user to team
        await apiService.createUserTeamRelation({
          user_id: user.id,
          team_id: team.id
        });

        updateUser({
          ...user,
          hasTeam: true,
          teamId: team.id
        });

        navigate('/dashboard');
      } else {
        setError('Team mit diesem Code nicht gefunden');
      }
    } catch (error) {
      console.error('Join team error:', error);
      setError('Fehler beim Beitreten zum Team');
    } finally {
      setLoading(false);
    }
  };

  if (!mode) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              Team Setup
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              Erstellen Sie ein neues Team oder treten Sie einem bestehenden bei
            </p>
          </div>
          
          <div className="space-y-4">
            <button
              onClick={() => setMode('create')}
              className="w-full flex items-center justify-center px-4 py-4 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
            >
              <SafeIcon icon={FiPlus} className="w-6 h-6 mr-3 text-blue-600" />
              <div className="text-left">
                <div className="font-medium">Neues Team erstellen</div>
                <div className="text-sm text-gray-500">Sie werden Team-Owner</div>
              </div>
            </button>
            
            <button
              onClick={() => setMode('join')}
              className="w-full flex items-center justify-center px-4 py-4 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
            >
              <SafeIcon icon={FiUsers} className="w-6 h-6 mr-3 text-green-600" />
              <div className="text-left">
                <div className="font-medium">Bestehendem Team beitreten</div>
                <div className="text-sm text-gray-500">Mit 8-stelligem Team-Code</div>
              </div>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <button
            onClick={() => setMode('')}
            className="mb-4 text-blue-600 hover:text-blue-500 text-sm"
          >
            ← Zurück
          </button>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            {mode === 'create' ? 'Team erstellen' : 'Team beitreten'}
          </h2>
        </div>
        
        <form onSubmit={mode === 'create' ? handleCreateTeam : handleJoinTeam} className="mt-8 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
              {error}
            </div>
          )}
          
          {mode === 'create' ? (
            <div>
              <label htmlFor="teamName" className="block text-sm font-medium text-gray-700">
                Team-Name
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <SafeIcon icon={FiUsers} className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="teamName"
                  name="teamName"
                  type="text"
                  required
                  value={formData.teamName}
                  onChange={handleChange}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Mein Team"
                />
              </div>
            </div>
          ) : (
            <div>
              <label htmlFor="teamCode" className="block text-sm font-medium text-gray-700">
                Team-Code (8 Zeichen)
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <SafeIcon icon={FiKey} className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="teamCode"
                  name="teamCode"
                  type="text"
                  required
                  maxLength={8}
                  value={formData.teamCode}
                  onChange={handleChange}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 uppercase"
                  placeholder="ABC12345"
                />
              </div>
            </div>
          )}

          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading 
                ? (mode === 'create' ? 'Team wird erstellt...' : 'Trete bei...') 
                : (mode === 'create' ? 'Team erstellen' : 'Team beitreten')
              }
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TeamSetup;